/**
 * Validation Utilities
 * Email, password, and form validation helpers with Zod schemas
 */

import { z } from 'zod';

/**
 * Email validation regex
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  return EMAIL_REGEX.test(email);
};

/**
 * Validate password strength
 * Requirements: min 8 chars, 1 uppercase, 1 lowercase, 1 number
 */
export const isStrongPassword = (password: string): boolean => {
  const minLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  
  return minLength && hasUppercase && hasLowercase && hasNumber;
};

/**
 * Get password strength message
 */
export const getPasswordStrengthMessage = (password: string): string => {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain a lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain a number';
  return '';
};

// Zod Schemas for Forms

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain an uppercase letter')
    .regex(/[a-z]/, 'Password must contain a lowercase letter')
    .regex(/[0-9]/, 'Password must contain a number'),
});

export const classSchema = z.object({
  className: z.string().min(1, 'Class name is required'),
  gradeLevel: z.string().min(1, 'Year level is required'),
  ageRating: z.number().min(5).max(18),
  teacherId: z.string().optional(),
  settings: z.object({
    allowStorySaving: z.boolean(),
    maxStoriesPerStudent: z.number().min(0),
    requireParentEmail: z.boolean(),
  }),
});

export const studentSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  classId: z.string().min(1, 'Class is required'),
  username: z.string().optional(),
  password: z.string().optional(),
  parentEmail: z.string().email('Invalid email address').optional().or(z.literal('')),
  ageRating: z.number().min(5).max(18).optional(),
});

export const wordListSchema = z.object({
  listName: z.string().min(1, 'List name is required'),
  classId: z.string().min(1, 'Class is required'),
  words: z.array(z.string()).min(1, 'At least one word is required'),
  setAsActive: z.boolean().optional(),
  dueDate: z.string().optional(),
});

export const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
});

export type LoginFormData = z.infer<typeof loginSchema>;
export type RegisterFormData = z.infer<typeof registerSchema>;
export type ClassFormData = z.infer<typeof classSchema>;
export type StudentFormData = z.infer<typeof studentSchema>;
export type WordListFormData = z.infer<typeof wordListSchema>;
export type ProfileFormData = z.infer<typeof profileSchema>;
